import { Delete, Edit, PictureAsPdf, Visibility } from '@mui/icons-material';
import { status } from 'adminConstants/investorReporting';
import { InvestorReportingType } from 'further-types/investor-reporting';
import {
  createCustomUpdateViewRoute,
  createCustomUpdateEditRoute,
  createDeploymentUpdateViewRoute,
  createExitStatementPdfDownloadRoute,
  createExitStatementViewRoute,
  createExitStatementAddRoute,
  createInvestorReportingEditRoute,
  createInvestorReportingPdfDownloadRoute,
  createInvestorReportingViewRoute,
  createUpdateExitStatementRoute,
} from 'adminConstants/routes';
import { TableActions } from 'components/Table';
import { useDisclosure } from 'further-ui/hooks';
import React from 'react';

type Props = {
  investorReport: {
    _id: string;
    updateType: string;
    status: string;
    viewable: boolean;
    affectedCompanies?: Array<{ id: string; name: string }>;
    exitId?: string;
    exitAmendmentId?: string;
  };
  canUpdate: boolean;
  canDelete: boolean;
  deleteDisclosure: ReturnType<typeof useDisclosure>;
};

const ActionTools: React.FC<Props> = ({
  investorReport,
  canUpdate,
  canDelete,
  deleteDisclosure,
}) => {
  const isCustomUpdate =
    investorReport.updateType === InvestorReportingType.CustomUpdate;
  const isDeploymentUpdate =
    investorReport.updateType === InvestorReportingType.DeploymentUpdate;
  const isPeriodicUpdate =
    investorReport.updateType === InvestorReportingType.Periodic;
  const isExitStatement =
    investorReport.updateType === InvestorReportingType.ExitStatement;
  const isSubmitted = investorReport.status === status.SUBMITTED;
  const isDraftExitStatement =
    isExitStatement && investorReport.status === 'DRAFT';

  const showViewButton =
    investorReport.viewable ||
    isCustomUpdate ||
    isDeploymentUpdate ||
    (isExitStatement && !isDraftExitStatement);
  const viewButtonLink = isCustomUpdate
    ? createCustomUpdateViewRoute(investorReport._id)
    : isDeploymentUpdate
    ? createDeploymentUpdateViewRoute(investorReport._id)
    : isExitStatement
    ? createExitStatementViewRoute(investorReport._id)
    : createInvestorReportingViewRoute(investorReport._id);

  const editButtonLink = isDraftExitStatement
    ? createExitStatementAddRoute(
        investorReport.exitId || '',
        investorReport.exitAmendmentId || undefined,
      )
    : isExitStatement
    ? createUpdateExitStatementRoute(investorReport._id)
    : isCustomUpdate
    ? createCustomUpdateEditRoute(investorReport._id)
    : createInvestorReportingEditRoute(investorReport._id);

  const showEditButton = canUpdate && !isDeploymentUpdate;
  const showDeleteButton = canDelete && !isDeploymentUpdate;

  return (
    <TableActions
      showAsDropdown
      actions={[
        {
          label: 'View',
          icon: Visibility,
          color: 'primary',
          link: viewButtonLink,
          visible: showViewButton,
        },
        {
          label: isDraftExitStatement ? 'Edit draft' : 'Edit',
          icon: Edit,
          color: 'primary',
          link: editButtonLink,
          visible: showEditButton,
        },
        {
          label: 'Delete',
          icon: Delete,
          color: 'error',
          onClick: () =>
            deleteDisclosure.stageAction({
              id: investorReport._id,
              isExitStatement,
              isCustomUpdate,
              isPeriodicUpdate,
              affectedCompanies: investorReport.affectedCompanies,
            }),
          visible: showDeleteButton,
        },
        {
          label: 'Download PDFs',
          icon: PictureAsPdf,
          color: 'primary',
          link: createInvestorReportingPdfDownloadRoute(investorReport._id),
          visible: isPeriodicUpdate && isSubmitted,
        },
        {
          label: 'Download PDFs',
          icon: PictureAsPdf,
          color: 'primary',
          link: createExitStatementPdfDownloadRoute(investorReport._id),
          visible: isExitStatement,
        },
      ]}
    />
  );
};

export default ActionTools;
